import {noop} from '@benchling/js-utils';
import {Box} from '@mantine/core';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {
  ExperimentalInputsComponentName,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {useInputTable} from 'procedures/planning/useInputTable';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';

import {ExperimentFlowchart} from './ExperimentalInputs';
import NoParametersWarningBanner from './NoParametersWarningBanner';

interface ProcedureRunDesignEditorRightSidepanelProps {
  templateFlowchart: ExperimentFlowchart;
  selectedMethodId: string;
  procedureType: ProcedureType;
  context: ExperimentalInputsContextProperties;
}

export default function ProcedureRunDesignEditorRightSidepanel(
  props: ProcedureRunDesignEditorRightSidepanelProps
): JSX.Element {
  const {context} = props;
  const selectedMethod = props.templateFlowchart.nodes.find((m) => m.id === props.selectedMethodId);
  assert(selectedMethod != null, 'Selected method should not be null');
  const options = {context, selectedMethod, locked: true, procedureType: props.procedureType};
  const parameterTable = useInputTable(ExperimentalInputsComponentName.PARAMETERS, options);
  const materialInputsTable = useInputTable(ExperimentalInputsComponentName.MATERIAL_INPUTS, options);
  const equipmentTable = useInputTable(ExperimentalInputsComponentName.EQUIPMENT, options);
  const conditionsText = getProcedureRunConditionTextForProcedureType(props.procedureType, {plural: true});

  const shouldRenderAnyTable = parameterTable || materialInputsTable || equipmentTable;

  const value = useMemo(
    () => ({
      procedureValidationErrors: Object.entries(context.evaluatedValues).flatMap(([methodId, values]) =>
        values.blockingValueErrorCount > 0
          ? [
              {
                errorMessage: `Some ${conditionsText} have invalid values specified`,
                type: ProcedureValidationErrorType.MISMATCHED_VALUE_TYPE,
                primaryObject: {id: methodId, objectType: ProcedureValidationObjectType.METHOD},
                parentObjects: [],
              } satisfies ProcedureValidationError,
            ]
          : []
      ),
      anyTabValidationErrors: false,
      setAnyTabValidationErrors: noop,
    }),
    [conditionsText, context.evaluatedValues]
  );

  return (
    <ProcedureValidationContext.Provider value={value}>
      <Box p="lg">
        {shouldRenderAnyTable ? (
          <>
            {materialInputsTable}
            {parameterTable}
            {equipmentTable}
          </>
        ) : (
          <NoParametersWarningBanner procedureType={props.procedureType} conditionsText={conditionsText} />
        )}
      </Box>
    </ProcedureValidationContext.Provider>
  );
}
